/**
 * Client-side parser for IdP SAML metadata XML (the document Okta/Entra/Google
 * Workspace expose for an app) so the owner can autofill the SSO settings form
 * instead of hand-copying entity ID, sign-in URL, and certificate. Pure
 * DOMParser — the XML never leaves the browser.
 */

export interface ParsedIdpMetadata {
  entityId: string | null;
  ssoUrl: string | null;
  /** Signing certificates, base64 bodies without PEM armor, in document order. */
  certificates: string[];
}

function byLocalName(root: ParentNode, name: string): Element[] {
  const out: Element[] = [];
  const walk = (node: ParentNode) => {
    for (const child of Array.from(node.children ?? [])) {
      if (child.localName === name) out.push(child);
      walk(child);
    }
  };
  walk(root);
  return out;
}

export function parseIdpMetadataXml(xml: string): ParsedIdpMetadata | null {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, "application/xml");
  } catch {
    return null;
  }
  if (doc.getElementsByTagName("parsererror").length > 0) {
    return null;
  }

  const descriptor =
    doc.documentElement?.localName === "EntityDescriptor"
      ? doc.documentElement
      : byLocalName(doc, "EntityDescriptor")[0];
  if (!descriptor) {
    return null;
  }

  const entityId = descriptor.getAttribute("entityID")?.trim() || null;

  const idp = byLocalName(descriptor, "IDPSSODescriptor")[0] ?? descriptor;

  // node-saml sends the AuthnRequest over the redirect binding, so prefer
  // HTTP-Redirect and fall back to HTTP-POST.
  const ssoServices = byLocalName(idp, "SingleSignOnService");
  const redirect = ssoServices.find((el) =>
    (el.getAttribute("Binding") ?? "").endsWith("HTTP-Redirect"),
  );
  const post = ssoServices.find((el) =>
    (el.getAttribute("Binding") ?? "").endsWith("HTTP-POST"),
  );
  const ssoUrl = (redirect ?? post)?.getAttribute("Location")?.trim() || null;

  const certificates: string[] = [];
  for (const keyDescriptor of byLocalName(idp, "KeyDescriptor")) {
    const use = keyDescriptor.getAttribute("use");
    if (use && use !== "signing") continue;
    // "X509Certificate" is a SAML element name, not a credential.
    for (const cert of byLocalName(keyDescriptor, "X509Certificate")) { // gitleaks:allow
      const body = (cert.textContent ?? "").replace(/\s+/g, "");
      if (body.length > 0 && !certificates.includes(body)) {
        certificates.push(body);
      }
    }
  }

  if (!entityId && !ssoUrl && certificates.length === 0) {
    return null;
  }
  return { entityId, ssoUrl, certificates };
}
